import {
	AsYouType,
	getCountries,
	getCountryCallingCode,
	isValidPhoneNumber,
	parsePhoneNumberFromString,
} from "libphonenumber-js/min";

const PHONE_MIN_DIGITS = 7;
const PHONE_MAX_DIGITS = 15;

export type PhoneCountryCodeOption = {
	value: string;
	label: string;
};

export const DEFAULT_PHONE_COUNTRY_CODE = "+58";

/**
 * Whitelist de países por ISO-2 (ej: ["VE", "CO"]).
 * Si un país no está aquí, no aparece en el selector.
 */
export const PHONE_ALLOWED_COUNTRY_ISO: readonly string[] = ["VE"];

/**
 * Whitelist explícita de códigos de llamada (ej: ["+58", "+57"]).
 * Se combina con PHONE_ALLOWED_COUNTRY_ISO.
 */
export const PHONE_ALLOWED_CALLING_CODES: readonly string[] = ["+58"];

const regionDisplayNames =
	typeof Intl !== "undefined" && "DisplayNames" in Intl
		? new Intl.DisplayNames(["es"], { type: "region" })
		: null;

function getFlagFromIso(countryIso: string): string {
	if (!/^[A-Z]{2}$/.test(countryIso)) return "";
	const A_CODE_POINT = 65;
	const REGIONAL_INDICATOR_A = 127462;
	const chars = countryIso
		.toUpperCase()
		.split("")
		.map((char) =>
			String.fromCodePoint(
				REGIONAL_INDICATOR_A + (char.charCodeAt(0) - A_CODE_POINT),
			),
		);
	return chars.join("");
}

function formatFlag(countryIso: string): string {
	const flag = getFlagFromIso(countryIso);
	return flag || countryIso;
}

function getCountryName(countryIso: string): string {
	return regionDisplayNames?.of(countryIso) || countryIso;
}

function formatFlagWithCountryName(countryIso: string): string {
	return `${formatFlag(countryIso)} ${getCountryName(countryIso)}`;
}

function getCallingCodeLabel(code: string, countriesIso: string[]): string {
	if (countriesIso.length === 0) return code;
	if (countriesIso.length === 1)
		return `${formatFlagWithCountryName(countriesIso[0])} (${code})`;
	if (countriesIso.length === 2) {
		return `${formatFlagWithCountryName(countriesIso[0])} / ${formatFlagWithCountryName(countriesIso[1])} (${code})`;
	}
	const firstTwo = countriesIso.slice(0, 2).map(formatFlagWithCountryName).join(" / ");
	return `${firstTwo} +${countriesIso.length - 2} (${code})`;
}

const allowedCountryIsoSet = new Set(
	PHONE_ALLOWED_COUNTRY_ISO.map((country) => country.trim().toUpperCase()),
);
const allowedCallingCodeSet = new Set(
	PHONE_ALLOWED_CALLING_CODES.map((code) =>
		normalizePhoneCountryCode(code, DEFAULT_PHONE_COUNTRY_CODE),
	),
);

const callingCodeToCountryIso = new Map<string, string[]>();
for (const country of getCountries()) {
	const callingCode = `+${getCountryCallingCode(country)}`;
	const isCountryAllowed =
		allowedCountryIsoSet.size === 0 || allowedCountryIsoSet.has(country);
	const isCallingCodeAllowed =
		allowedCallingCodeSet.size === 0 || allowedCallingCodeSet.has(callingCode);

	if (!isCountryAllowed && !isCallingCodeAllowed) continue;

	const current = callingCodeToCountryIso.get(callingCode) || [];
	if (!current.includes(country)) {
		current.push(country);
	}
	callingCodeToCountryIso.set(callingCode, current);
}

const ALL_CALLING_CODES = Array.from(callingCodeToCountryIso.keys()).sort(
	(a, b) => Number(a.replace(/\D/g, "")) - Number(b.replace(/\D/g, "")),
);

export const PHONE_COUNTRY_CODE_OPTIONS: readonly PhoneCountryCodeOption[] =
	ALL_CALLING_CODES.map((code) => ({
		value: code,
		label: getCallingCodeLabel(code, callingCodeToCountryIso.get(code) || []),
	}));

const allowedCallingCodeValueSet = new Set(
	PHONE_COUNTRY_CODE_OPTIONS.map((option) => option.value),
);

function ensureDefaultCountryCode(
	options: PhoneCountryCodeOption[],
): PhoneCountryCodeOption[] {
	if (options.length === 0) {
		return [{ value: DEFAULT_PHONE_COUNTRY_CODE, label: DEFAULT_PHONE_COUNTRY_CODE }];
	}
	if (options.some((option) => option.value === DEFAULT_PHONE_COUNTRY_CODE)) {
		return options;
	}
	return [
		{ value: DEFAULT_PHONE_COUNTRY_CODE, label: DEFAULT_PHONE_COUNTRY_CODE },
		...options,
	];
}

function normalizeAllowedPhoneCountryCode(
	rawValue: string,
	fallback = DEFAULT_PHONE_COUNTRY_CODE,
): string {
	const normalized = normalizePhoneCountryCode(rawValue, fallback);
	if (allowedCallingCodeValueSet.has(normalized)) return normalized;

	const normalizedFallback = normalizePhoneCountryCode(
		fallback,
		DEFAULT_PHONE_COUNTRY_CODE,
	);
	if (allowedCallingCodeValueSet.has(normalizedFallback)) return normalizedFallback;

	return PHONE_COUNTRY_CODE_OPTIONS[0]?.value || normalizedFallback;
}

/**
 * Formats a phone input to the local Venezuelan format: XXXX-XXXXXXX
 * (4 digits, auto-hyphen, up to 7 more digits).
 */
export function formatLocalPhoneInput(rawValue: string): string {
	return sanitizePhoneLocalInput(rawValue);
}

export function normalizePhoneCountryCode(
	rawValue: string,
	fallback = DEFAULT_PHONE_COUNTRY_CODE,
): string {
	const cleaned = rawValue.trim().replace(/[^\d+]/g, "");
	if (!cleaned) return fallback;
	if (/^\+\d{1,4}$/.test(cleaned)) return cleaned;
	if (/^\d{1,4}$/.test(cleaned)) return `+${cleaned}`;
	return fallback;
}

export function sanitizePhoneLocalInput(rawValue: string): string {
	return rawValue.replace(/\D/g, "").slice(0, PHONE_MAX_DIGITS);
}

export function splitPhoneWithCountryCode(
	rawValue: string,
	fallback = DEFAULT_PHONE_COUNTRY_CODE,
): { countryCode: string; localNumber: string } {
	return splitPhoneInputValue(rawValue, fallback);
}

export function splitPhoneInputValue(
	rawValue: string,
	fallback = DEFAULT_PHONE_COUNTRY_CODE,
	options?: {
		inferCallingCodeFromPlainDigits?: boolean;
		codeOnlyMeansEmptyLocal?: boolean;
	},
): { countryCode: string; localNumber: string } {
	const value = rawValue.trim();
	const normalizedFallback = normalizePhoneCountryCode(fallback);
	const inferCallingCodeFromPlainDigits =
		options?.inferCallingCodeFromPlainDigits ?? true;
	const codeOnlyMeansEmptyLocal = options?.codeOnlyMeansEmptyLocal ?? true;
	const hasExplicitInternationalPrefix = value.startsWith("+");
	if (!value) return { countryCode: normalizedFallback, localNumber: "" };

	const parsed = parsePhoneNumberFromString(value);
	if (parsed) {
		const parsedCountryCode = `+${parsed.countryCallingCode}`;
		return {
			countryCode: normalizeAllowedPhoneCountryCode(
				parsedCountryCode,
				normalizedFallback,
			),
			localNumber: parsed.nationalNumber,
		};
	}

	const digitsOnly = value.replace(/\D/g, "");
	if (!digitsOnly) return { countryCode: normalizedFallback, localNumber: "" };
	if (!inferCallingCodeFromPlainDigits) {
		return {
			countryCode: normalizedFallback,
			localNumber: sanitizePhoneLocalInput(digitsOnly),
		};
	}

	const candidates = ensureDefaultCountryCode([...PHONE_COUNTRY_CODE_OPTIONS]).sort(
		(a, b) => b.value.replace(/\D/g, "").length - a.value.replace(/\D/g, "").length,
	);

	if (codeOnlyMeansEmptyLocal) {
		for (const option of candidates) {
			const codeDigits = option.value.replace(/\D/g, "");
			if (digitsOnly === codeDigits) {
				return {
					countryCode: normalizeAllowedPhoneCountryCode(
						option.value,
						normalizedFallback,
					),
					localNumber: "",
				};
			}
		}
	}

	for (const option of candidates) {
		const codeDigits = option.value.replace(/\D/g, "");
		if (!digitsOnly.startsWith(codeDigits)) continue;
		const localNumber = digitsOnly.slice(codeDigits.length);
		if (!hasExplicitInternationalPrefix && localNumber.length < PHONE_MIN_DIGITS) {
			continue;
		}
		return {
			countryCode: normalizeAllowedPhoneCountryCode(
				option.value,
				normalizedFallback,
			),
			localNumber,
		};
	}

	return {
		countryCode: normalizedFallback,
		localNumber: sanitizePhoneLocalInput(digitsOnly),
	};
}

export function composePhoneWithCountryCode(
	countryCode: string,
	localNumber: string,
): string {
	const normalizedCountryCode = normalizeAllowedPhoneCountryCode(countryCode);
	const normalizedLocalNumber = sanitizePhoneLocalInput(localNumber);
	if (!normalizedLocalNumber) return "";

	const fullDigits = `${normalizedCountryCode}${normalizedLocalNumber}`.replace(
		/\D/g,
		"",
	);
	const asYouType = new AsYouType();
	const formatted = asYouType.input(`+${fullDigits}`);
	const parsed = parsePhoneNumberFromString(formatted || `+${fullDigits}`);

	return parsed?.number || formatted || `+${fullDigits}`;
}

export function getPhoneCountryCodeOptions(
	selectedCode?: string,
): PhoneCountryCodeOption[] {
	const baseOptions = ensureDefaultCountryCode([...PHONE_COUNTRY_CODE_OPTIONS]);
	if (!selectedCode) return baseOptions;
	return baseOptions;
}

export function countPhoneDigits(value: string): number {
	return (value.match(/\d/g) ?? []).length;
}

export function sanitizePhoneInput(rawValue: string): string {
	const parsed = parsePhoneNumberFromString(rawValue);
	if (parsed) return parsed.number;

	const raw = rawValue.trim();
	if (!raw) return "";

	if (raw.startsWith("+")) {
		const normalized = `+${raw.replace(/\D/g, "")}`;
		const asYouType = new AsYouType();
		return asYouType.input(normalized) || normalized;
	}

	return sanitizePhoneLocalInput(raw);
}

export function getPhoneValidationError(
	value: string,
	options?: { required?: boolean },
): string | null {
	const phone = value.trim();
	if (!phone) {
		return options?.required ? "Este campo es obligatorio" : null;
	}

	const digits = countPhoneDigits(phone);
	if (digits < PHONE_MIN_DIGITS || digits > PHONE_MAX_DIGITS) {
		return "El teléfono debe tener entre 7 y 15 dígitos";
	}

	if (!phone.startsWith("+")) {
		return "Selecciona un código de país y verifica el número";
	}

	if (!isValidPhoneNumber(phone)) {
		return "Ingresa un teléfono válido para el país seleccionado";
	}

	return null;
}
